/**
 * Hostaway Status Bucket Model
 * 
 * Maps Hostaway reservation statuses to logical buckets for Owner Portal:
 * - BOOKED: Revenue-eligible reservations (new, modified)
 * - OWNER_STAY: Owner stays (non-revenue, blocks calendar)
 * - CANCELLED: Cancelled reservations (excluded from revenue + nights)
 * - INQUIRY: Inquiry/pending reservations (excluded from revenue + nights)
 * 
 * Based on DB evidence from 2026-01-12 deep dive:
 * Statuses found: inquiry, modified, new, ownerStay, cancelled, inquiryNotPossible, expired, declined, inquiryPreapproved
 */

/**
 * Status bucket definitions
 */
export const STATUS_BUCKETS = {
  BOOKED: {
    key: 'BOOKED',
    label: 'Booked',
    statuses: ['new', 'modified'],
    revenueEligible: true,
    includedInKPIs: true,
  },
  OWNER_STAY: {
    key: 'OWNER_STAY',
    label: 'Owner Stay',
    statuses: ['ownerstay', 'ownerStay', 'OWNERSTAY', 'OWNER_STAY', 'owner_stay'], // Handle all case variants
    revenueEligible: false,
    includedInKPIs: false,
  },
  CANCELLED: {
    key: 'CANCELLED',
    label: 'Cancelled',
    statuses: ['cancelled', 'canceled'],
    revenueEligible: false,
    includedInKPIs: false,
  },
  INQUIRY: {
    key: 'INQUIRY',
    label: 'Inquiry',
    statuses: [
      'inquiry',
      'inquirynotpossible',
      'inquirypreapproved',
      'inquirypending',
      'pending',
      'request',
      'awaiting_approval',
      'declined',
      'expired',
    ],
    revenueEligible: false,
    includedInKPIs: false,
  },
};

/**
 * Normalize status string (trim, lowercase)
 * @param {string} status - Raw status from API
 * @returns {string} - Normalized status
 */
export function normalizeStatus(status) {
  if (!status || typeof status !== 'string') {
    return 'unknown';
  }
  return status.trim().toLowerCase();
}

/**
 * Get status bucket for a given status
 * @param {string} status - Raw status from API
 * @returns {string} - Bucket key (BOOKED, OWNER_STAY, CANCELLED, INQUIRY, or 'unknown')
 */
export function getStatusBucket(status) {
  const normalized = normalizeStatus(status);
  
  for (const [bucketKey, bucket] of Object.entries(STATUS_BUCKETS)) {
    if (bucket.statuses.includes(normalized)) {
      return bucketKey;
    }
  }
  
  return 'unknown';
}

/**
 * Check if status is booked (revenue-eligible)
 * @param {string} status - Raw status from API
 * @returns {boolean}
 */
export function isBookedStatus(status) {
  return getStatusBucket(status) === 'BOOKED';
}

/**
 * Check if status is owner stay
 * @param {string} status - Raw status from API
 * @returns {boolean}
 */
export function isOwnerStayStatus(status) {
  return getStatusBucket(status) === 'OWNER_STAY';
}

/**
 * Check if status is cancelled
 * @param {string} status - Raw status from API
 * @returns {boolean}
 */
export function isCancelledStatus(status) {
  return getStatusBucket(status) === 'CANCELLED';
}

/**
 * Check if status is inquiry/pending
 * @param {string} status - Raw status from API
 * @returns {boolean}
 */
export function isInquiryStatus(status) {
  return getStatusBucket(status) === 'INQUIRY';
}

/**
 * Check if status should be included in revenue/nights KPIs
 * @param {string} status - Raw status from API
 * @returns {boolean}
 */
export function isIncludedInKPIs(status) {
  return isBookedStatus(status);
}

/**
 * Get all statuses for a bucket
 * @param {string} bucketKey - Bucket key (BOOKED, OWNER_STAY, etc.)
 * @returns {string[]} - Array of normalized status strings
 */
export function getStatusesForBucket(bucketKey) {
  const bucket = STATUS_BUCKETS[bucketKey];
  return bucket ? bucket.statuses : [];
}

/**
 * Filter reservations by bucket
 * @param {Array} reservations - Array of reservation objects
 * @param {string} bucketKey - Bucket key (BOOKED, OWNER_STAY, etc., or 'ALL')
 * @returns {Array} - Filtered reservations
 */
export function filterReservationsByBucket(reservations, bucketKey) {
  if (!Array.isArray(reservations)) {
    return [];
  }
  
  if (bucketKey === 'ALL') {
    return reservations;
  }
  
  const statuses = getStatusesForBucket(bucketKey);
  if (statuses.length === 0) {
    return reservations; // Unknown bucket, return all
  }
  
  return reservations.filter(reservation => {
    const status = reservation?.status;
    const normalized = normalizeStatus(status);
    return statuses.includes(normalized);
  });
}

/**
 * Get booked reservations only (for KPI calculations)
 * @param {Array} reservations - Array of reservation objects
 * @returns {Array} - Booked reservations only
 */
export function getBookedReservations(reservations) {
  return filterReservationsByBucket(reservations, 'BOOKED');
}

/**
 * Get status group options for filter dropdown
 * @returns {Array} - Array of {value, label} objects
 */
export function getStatusGroupOptions() {
  return [
    { value: 'BOOKED', label: 'Booked' },
    { value: 'OWNER_STAY', label: 'Owner Stays' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'INQUIRY', label: 'Inquiries' },
    { value: 'ALL', label: 'All' },
  ];
}
